import { useMemo } from 'react'
import Joi from 'joi'
import { useQuery } from '@apollo/client'
import {
  compact,
  flatMap,
  uniq,
  isEmpty,
  get,
  find,
  head,
  map,
  toNumber,
  isNaN,
  isNil,
  isUndefined,
} from 'lodash'
import uuid from 'react-uuid'
import remark from 'remark'
import visit from 'unist-util-visit'

import { KG_NODES, useQueryKG, useGetConnectedNodes, namespaces } from '@common'
import {
  addSchema,
  createBlankDoc,
  schemaToPropTypes,
  matchStateSchema,
  ticketDocSchema,
  UniqueStringArray,
  SchemaEmptyPositive,
  NullableString,
  shortString,
  RequiredString,
  mongoIdString,
  kgNodeIdString,
  UniqueNodeArray,
  orderedArray,
  SchemaEmptyNonNegative,
} from '@schema'
import { cceClient } from '@gql'

import { _queryBenefit, _query_kg } from './schema.gql'

export function useReqestVariableNode(request_type, fieldname, filterByType) {
  const { data } = useQueryKG(_query_kg, {
    variables: {
      query: {
        op: 'eq',
        field: 'node_id',
        value: request_type,
      },
    },
    skip: isEmpty(request_type),
  })

  const result = useMemo(() => {
    if (!data) return null
    const node = head(data.query_kg)
    if (filterByType) {
      if (
        get(find(node.attributes, { name: 'type' }), 'values.0') !== fieldname
      ) {
        return []
      }
    }
    return get(find(node.attributes, { name: fieldname }), 'values', [])
  }, [data, fieldname, filterByType])

  return result
}

export const AmountOptionType = Object.freeze({
  main: {
    name: 'range',
    label: '範圍',
  },
  sub: {
    name: 'list',
    label: '列表',
  },
})

const optionValueSchema = {
  value_type: {
    schema: Joi.string()
      .valid('id', 'const', 'empty')
      .required(),
    defaultValue: 'empty',
    type: 'string',
    label: '保額型態',
  },
  option_id: {
    schema: Joi.string(),
    type: 'string',
    label: '給付金額',
  },
  plan_ratio: {
    schema: SchemaEmptyPositive,
    type: 'number',
    label: '倍率',
  },
  constant: {
    schema: SchemaEmptyPositive,
    type: 'number',
    label: '常數保額',
  },
}

export const amountOptionProtocol = 'option:'

function extractOptionIds() {
  return function transformer(tree, file) {
    visit(tree, node => {
      if (isNil(file.data.options)) file.data.options = []
      if (node.type === 'link') {
        const { protocol, pathname: option_id, searchParams } = new URL(
          get(node, 'url'),
        )
        if (protocol === amountOptionProtocol) {
          file.data.options.push({
            searchParams,
            option_id,
          })
        }
      }
    })
  }
}

export function computeAmount(amountOption, multiplier) {
  const minAmount = toNumber(
    get(amountOption, `${get(amountOption, 'type')}.0`),
  )
  return {
    minAmount,
    multiplier: isUndefined(multiplier) ? NaN : multiplier,
    amount: minAmount * toNumber(multiplier),
  }
}

export function isValidAmount(amount, multiplier) {
  return !isNaN(amount) && amount >= 0 && toNumber(multiplier) > 0
}

export const benefitsRefSchema = {
  benefit_id: {
    schema: RequiredString.max(mongoIdString),
    type: 'string',
    label: 'benefit代碼',
  },
  display_name: {
    schema: Joi.string(),
    type: 'string',
    label: '顯示名稱',
  },
  dm_brief: {
    schema: Joi.string()
      .allow('')
      .allow(null)
      .custom((value, helpers) => {
        const amountOptions = get(helpers, 'prefs.context.amountOptions', [])
        const {
          data: { options },
        } = remark()
          .use(extractOptionIds)
          .processSync(value)
        const errors = compact(
          map(options, ({ option_id, searchParams }) => {
            const amountOption = find(amountOptions, { option_id })
            if (isNil(amountOption)) return `${option_id} not found`

            const { amount } = computeAmount(
              amountOption,
              searchParams.get('multiplier'),
            )
            if (!isValidAmount(amount, searchParams.get('multiplier')))
              return `${amount} is invalid`
          }),
        )
        return isEmpty(errors) ? value : helpers.message(errors.join('\n'))
      }),
    type: 'string',
    label: 'DM給付摘要',
  },
  dm_note: {
    schema: Joi.string(),
    type: 'string',
    label: 'DM給付備註',
  },
  request_unit: {
    label: '申請單位',
    schema: NullableString.max(shortString),
    type: 'string',
  },
  ...optionValueSchema,
}

export const amountOptionSchema = {
  name: {
    schema: RequiredString.max(shortString),
    type: 'string',
    label: '名稱',
  },
  type: {
    schema: Joi.string()
      .valid(...map(Object.values(AmountOptionType), 'name'))
      .required(),
    type: 'string',
    label: '型態',
    useNodes: () => {
      const types = useMemo(
        () =>
          Object.values(AmountOptionType).map(({ name, label }) => ({
            name: label,
            id: name,
          })),
        [],
      )

      return types
    },
  },
  range: {
    schema: Joi.array()
      .items(Joi.number())
      .max(3)
      .required(),
    type: 'arrayOf(number)',
    label: '範圍',
    useNodes: () => {
      const result = useMemo(() => {
        const values = [
          ['百萬', '1000000'],
          ['十萬', '100000'],
          ['一萬', '10000'],
        ]
        return map(values, ([label, value]) => ({ label, value }))
      }, [])

      return result
    },
    multiInput: true,
  },
  list: {
    schema: UniqueStringArray.required(),
    type: 'arrayOf(string)',
    label: '列表',
  },
  unit: {
    schema: RequiredString.max(kgNodeIdString),
    referToKG: 'insurance',
    type: 'node',
    label: '單位',
    useNodes: () => {
      const roots = useMemo(
        () => [
          {
            node_id: KG_NODES.insurance.AMOUNT_OPTION_UNIT_ROOT_ID,
            namespace: 'insurance',
          },
        ],
        [],
      )
      const nodes = useGetConnectedNodes(roots, true)
      return nodes
    },
  },
  option_id: {
    schema: RequiredString.max(shortString),
    type: 'string',
    label: '選項代碼',
    skipCompare: true,
    defaultValue: () => uuid(),
  },
}

export const requestVariableSchema = {
  request_type: {
    schema: RequiredString.max(kgNodeIdString),
    referToKG: 'insurance',
    type: 'node',
    label: '依據條件',
    useNodes: () => {
      const roots = useMemo(
        () => [
          {
            node_id: KG_NODES.insurance.REQUEST_VARIABLE_ROOT_ID,
            namespace: 'insurance',
          },
        ],
        [],
      )
      const nodes = useGetConnectedNodes(roots, true)
      return nodes
    },
  },
  range: {
    schema: Joi.array()
      .items(Joi.number().min(0))
      .custom(orderedArray())
      .min(2)
      .max(3)
      .required(),
    type: 'arrayOf(number)',
    label: '範圍(最小值/最大值/間距)',
    useNodes: ({ request_type }) => {
      const values = useReqestVariableNode(request_type, 'range', true)
      const result = useMemo(
        () =>
          map(values, value => ({
            label: value.toString(),
            value: value.toString(),
          })),
        [values],
      )

      return result
    },
    multiInput: true,
  },
  list: {
    schema: UniqueNodeArray.min(1).required(),
    type: 'arrayOf(string)',
    label: '列表',
    useNodes: ({ request_type }) => {
      const values = useReqestVariableNode(request_type, 'list', true)
      const result = useMemo(
        () =>
          map(values, node => ({
            name: node,
            id: node,
          })),
        [values],
      )
      return result
    },
  },
  variable_id: {
    schema: RequiredString.max(shortString),
    type: 'string',
    label: '變數代碼',
    skipCompare: true,
    defaultValue: () => uuid(),
  },
}

export const requestVariablesSchema = {
  request_variable: {
    schema: Joi.array().required(),
    type: 'arrayOf(requestVariableSchema)',
    label: '需求變數',
    defaultValue: map(
      [
        KG_NODES.insurance.REQUEST_VARIABLE.INSURE_DURATION,
        KG_NODES.insurance.REQUEST_VARIABLE.AGE,
        KG_NODES.insurance.REQUEST_VARIABLE.PAYMENT_YEARS,
      ],
      request_type => ({
        ...createBlankDoc(requestVariableSchema, false),
        request_type,
      }),
    ),
  },
}

export const axisSchema = {
  axis_id: {
    schema: RequiredString.max(shortString),
    referToKG: 'insurance',
    type: 'string',
    label: '維度代碼',
  },
  axis_value: {
    schema: Joi.string(),
    type: 'string',
    label: '維度值',
  },
  ...optionValueSchema,
}

export const feeTableRefSchema = {
  feetable_id: {
    schema: RequiredString.max(mongoIdString),
    type: 'string',
    label: '費率表代碼',
  },
  axes: {
    schema: Joi.array().required(),
    type: 'arrayOf(axisSchema)',
    label: '維度',
  },
}

export const planSchema = {
  ...ticketDocSchema,
  ...matchStateSchema,
  name: {
    schema: RequiredString.max(shortString),
    type: 'string',
    label: '計劃名稱',
  },
  is_additional: {
    schema: Joi.bool(),
    type: 'bool',
    label: '附加條約',
  },
  benefits: {
    schema: Joi.array().required(),
    type: 'arrayOf(benefitsRefSchema)',
    label: '連結給付項目',
    useNodes: docs => {
      const doc_ids = useMemo(
        () => uniq(map(flatMap(docs, 'benefits'), 'benefit_id')),
        [docs],
      )

      const { data } = useQuery(_queryBenefit, {
        client: cceClient,
        variables: {
          doc_ids,
        },
        skip: isEmpty(doc_ids),
      })

      const benefitNames = useMemo(() => {
        if (isEmpty(doc_ids)) return []
        return !data
          ? null
          : data.queryBenefit.map(({ _id: id, benefit_name: name }) => ({
              id,
              name,
            }))
      }, [data, doc_ids])

      return benefitNames
    },
  },
  feetables: {
    schema: Joi.array().required(),
    type: 'arrayOf(feeTableRefSchema)',
    label: '連結費率表',
  },
  request_variables: {
    schema: Joi.array().required(),
    type: 'arrayOf(requestVariablesSchema)',
    label: '需求變數',
  },
  amount_options: {
    schema: Joi.array().required(),
    type: 'arrayOf(amountOptionSchema)',
    label: '保額選項',
  },
  benefit_restrict: {
    schema: NullableString.max(shortString),
    type: 'string',
    label: '給付限制',
  },
  spouse_age_bound: {
    schema: Joi.array()
      .custom(orderedArray())
      .items(SchemaEmptyNonNegative)
      .max(3),
    type: 'arrayOf(number)',
    label: '配偶可投保年齡(下限/上限/續保年齡)',
  },
  children_age_bound: {
    schema: Joi.array()
      .custom(orderedArray())
      .items(SchemaEmptyNonNegative)
      .max(3),
    type: 'arrayOf(number)',
    label: '子女可投保年齡(下限/上限/續保年齡)',
  },
  parent_age_bound: {
    schema: Joi.array()
      .custom(orderedArray())
      .items(SchemaEmptyNonNegative)
      .max(3),
    type: 'arrayOf(number)',
    label: '父母可投保年齡(下限/上限/續保年齡)',
  },
  assumed_interest: {
    schema: Joi.number()
      .min(0.0075)
      .max(0.05)
      .allow(null, ''),
    type: 'number',
    label: '保單預定利率',
    hint: 'empty/0.0075 ~ 0.05',
  },
}

addSchema({ amountOptionSchema })
addSchema({ benefitsRefSchema })
addSchema({ requestVariableSchema })
addSchema({ requestVariablesSchema })
addSchema({ feeTableRefSchema })
addSchema({ axisSchema })
addSchema({ planSchema }, '計畫', namespaces.insurance.value)

export const PlanPropType = schemaToPropTypes(planSchema)
